import React from 'react';
import { FileTextIcon, UploadCloudIcon, XIcon } from 'lucide-react';
import { validateUploadFile, formatFileSize } from '../../lib/careersSchema';
interface FileUploadProps {
  id: string;
  label: string;
  required?: boolean;
  file: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  /** Upload progress, 0 to 100, while the file is travelling to storage. */
  progress?: number | null;
  disabled?: boolean;
}

/**
 * Drag and drop file input with a click to browse fallback. The real input stays
 * in the tab order (visually hidden), so the drop zone is fully keyboard operable
 * and announced as a file input by screen readers.
 */
export function FileUpload({
  id,
  label,
  required,
  file,
  onChange,
  error,
  progress,
  disabled
}: FileUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const shownError = localError ?? error;
  const describedBy = [`${id}-hint`, shownError ? `${id}-error` : ''].filter(Boolean).join(' ');
  const accept = (candidate: File | undefined) => {
    if (!candidate) return;
    const problem = validateUploadFile(candidate);
    if (problem) {
      setLocalError(problem);
      onChange(null);
      return;
    }
    setLocalError(null);
    onChange(candidate);
  };
  const remove = () => {
    onChange(null);
    setLocalError(null);
    if (inputRef.current) inputRef.current.value = '';
    inputRef.current?.focus();
  };
  return <div data-id="element-666" data-mp="kut38r41uo39">
      <p className="block text-[14px] font-medium text-ink" id={`${id}-label`} data-id="element-667" data-mp="kv1gwl7zxxfi">
        {label}
        {!required && <span className="ml-1.5 font-normal text-muted" data-id="element-668" data-mp="yszrdfj759pq">optional</span>}
      </p>
      <p id={`${id}-hint`} className="mt-1 text-small text-muted" data-id="element-669" data-mp="twit3sia9eyx">
        PDF or Word (.docx), up to 5 MB.
      </p>

      {file ? <div className="mt-2 rounded-sm border border-line bg-charcoal p-4" data-id="element-670" data-mp="2vndtkxnfq2bz">
          <div className="flex items-center gap-3" data-id="element-671" data-mp="2vpgtc9ee39tw">
            <FileTextIcon className="h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-672" data-mp="zl0v0l2jf5oj" />
            <div className="min-w-0 flex-1" data-id="element-673" data-mp="14isq1nyhb7wy">
              <p className="truncate text-[15px] text-ink" data-id="element-674" data-mp="14jkmf0zwh9oa">{file.name}</p>
              <p className="text-small text-muted" data-id="element-675" data-mp="19hcafjxfcbn7">{formatFileSize(file.size)}</p>
            </div>
            <button type="button" onClick={remove} disabled={disabled} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-sm text-muted transition-colors duration-150 hover:bg-panel hover:text-ink disabled:opacity-50" aria-label={`Remove ${file.name}`} data-id="element-676" data-mp="1dl1vw0isyd40">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-677" data-mp="1x7q8br2ox4pp" />
            </button>
          </div>
          {typeof progress === 'number' && <div className="mt-3" data-id="element-678" data-mp="21awdeve2gpul">
              <div className="h-1.5 overflow-hidden rounded-full bg-panel" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-label={`Uploading ${file.name}`} data-id="element-679" data-mp="217qll8e1z9v6">
                <div className="h-full bg-gold transition-[width] duration-150 ease-out" style={{
            width: `${progress}%`
          }} data-id="element-680" data-mp="3hq0r3ik9wsqv" />
              </div>
              <p className="mt-1.5 text-small text-muted" data-id="element-681" data-mp="dbssx4l8wnre">
                {progress >= 100 ? 'Uploaded' : `Uploading, ${progress}%`}
              </p>
            </div>}
        </div> : <div className="mt-2" data-id="element-682" data-mp="4jigrhqsi87i">
          <input ref={inputRef} id={id} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="peer sr-only" aria-labelledby={`${id}-label`} aria-describedby={describedBy} aria-invalid={shownError ? true : undefined} aria-required={required} disabled={disabled} onChange={event => accept(event.target.files?.[0])} data-id="element-683" data-mp="4igtmewi1v9f" />
          <label htmlFor={id} onDragOver={event => {
        event.preventDefault();
        setDragging(true);
      }} onDragLeave={() => setDragging(false)} onDrop={event => {
        event.preventDefault();
        setDragging(false);
        accept(event.dataTransfer.files?.[0]);
      }} className={`flex min-h-[112px] cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border border-dashed px-4 py-6 text-center transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-gold peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-charcoal ${dragging ? 'border-gold bg-panel' : shownError ? 'border-[#FF8B7A] bg-charcoal' : 'border-line bg-charcoal hover:border-gold/70'}`} data-id="element-684" data-mp="gtyf4icnu7yv">
            <UploadCloudIcon className="h-6 w-6 text-gold" aria-hidden="true" data-id="element-685" data-mp="1oklz6kij7fei" />
            <span className="text-[15px] text-ink" data-id="element-686" data-mp="22br0rs5gmhrh">
              <span className="font-medium underline underline-offset-4" data-id="element-687" data-mp="22afmgyee1rm4">Choose a file</span> or drag it here
            </span>
          </label>
        </div>}

      {shownError && <p id={`${id}-error`} role="alert" className="mt-2 text-small text-[#FF8B7A]" data-id="element-688" data-mp="19o4jxi781gmo">
          {shownError}
        </p>}
    </div>;
}